/* HoneMD Treatments & Pricing controller (v2).
   v1 root cause: cards were "hidden" via the hidden attribute, but the section's
   `.hm-svc-card{display:flex}` author rule overrode the UA `[hidden]{display:none}`
   rule, so filtering updated state without ever changing the rendering. v2 pairs
   the attribute with an explicit `[hidden]{display:none!important}` rule and
   verifies visibility via computed styles in the audit.
   - Three-audience pricing: NEW CLIENT | STANDARD | MEMBER (radiogroup, arrows).
     LIST = compare-at || price. FIRST-VISIT = price (when compare-at). MEMBER =
     Math.round(LIST*0.8/100) dollars. Offers logic per state spec.
   - TREATMENT x GOAL intersection filtering, URL-param sync (+popstate),
     aria-live count, empty state with clear.
   - Utility bar docks below Dawn's sticky header via --hm-header-offset.
   - Book bar lifts above Shopify's preview toolbar and yields at the footer. */
(function () {
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- global: header offset for sticky docking ---------- */
  function watchHeader() {
    var sticky = document.querySelector('sticky-header');
    var headerSection = sticky ? sticky.closest('.shopify-section') : null;
    var header = document.querySelector('.header-wrapper');
    if (!headerSection || !header) return;
    function update() {
      var hidden = headerSection.classList.contains('shopify-section-header-hidden');
      var h = Math.round(hidden ? 0 : header.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--hm-header-offset', h + 'px');
      // Belt-and-suspenders: also set the docked bar's top inline.
      Array.prototype.forEach.call(document.querySelectorAll('.hm-svc__bar'), function (b) {
        b.style.top = h + 'px';
      });
    }
    new MutationObserver(update).observe(headerSection, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /* ---------- global: lift fixed UI above Shopify's draft preview toolbar ---------- */
  function watchPreviewBar() {
    function measure() {
      var bar = document.querySelector('#PBarNextFrameWrapper, #preview-bar-iframe, iframe[src*="preview_bar"]');
      var h = 0;
      if (bar) {
        var r = bar.getBoundingClientRect();
        if (r.height > 0 && r.height < 160 && r.bottom >= window.innerHeight - 4) h = Math.round(r.height);
      }
      document.documentElement.style.setProperty('--hm-preview-offset', h + 'px');
    }
    measure();
    setTimeout(measure, 1200);
    setTimeout(measure, 3000);
    window.addEventListener('resize', measure, { passive: true });
  }

  /* ---------- page controller ---------- */
  function init(root) {
    if (root.dataset.hmSvcBound) return;
    root.dataset.hmSvcBound = '1';

    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-hm-svc-card]'));
    var chips = Array.prototype.slice.call(root.querySelectorAll('[data-hm-svc-chip]'));
    var segs = Array.prototype.slice.call(root.querySelectorAll('[data-hm-svc-mode]'));
    var countLine = root.querySelector('[data-hm-svc-countline]');
    var grid = root.querySelector('[data-hm-svc-grid]');
    var empty = root.querySelector('[data-hm-svc-empty]');
    var total = cards.length;
    var state = { types: 'all', goals: 'all', mode: 'new-client' };

    /* ----- pricing ----- */
    function priceData(card) {
      var price = parseInt(card.dataset.price, 10);
      var compare = parseInt(card.dataset.compare, 10);
      var hasCompare = !isNaN(compare) && compare > price;
      var list = hasCompare ? compare : price;
      return {
        hasCompare: hasCompare,
        list: list,
        first: price,
        member: Math.round((list * 0.8) / 100) * 100
      };
    }
    function d(cents) { return '$' + Math.round(cents / 100); }

    function renderCardPrice(card) {
      var p = priceData(card);
      var label, html;
      if (state.mode === 'new-client') {
        if (p.hasCompare) {
          label = 'New client';
          // mirrors snippets/honemd-service-card.liquid - keep in lockstep (no strikethrough).
          html = '<strong class="hm-svc-card__now">' + d(p.first) + '</strong><span class="hm-svc-card__alt">Regular ' + d(p.list) + '</span>';
        } else {
          label = 'Price';
          html = '<strong class="hm-svc-card__now">' + d(p.list) + '</strong>';
        }
      } else if (state.mode === 'standard') {
        label = 'Price';
        html = '<strong class="hm-svc-card__now">' + d(p.list) + '</strong>';
      } else {
        label = 'Member';
        html = '<strong class="hm-svc-card__now">' + d(p.member) + '</strong><span class="hm-svc-card__alt">Regular ' + d(p.list) + '</span>';
      }
      card.querySelector('[data-hm-price-label]').textContent = label;
      card.querySelector('[data-hm-price]').innerHTML = html;
    }

    function renderPrices() {
      cards.forEach(function (card) {
        var row = card.querySelector('.hm-svc-card__pricing');
        if (reduceMotion) { renderCardPrice(card); return; }
        row.classList.add('is-updating');
        setTimeout(function () {
          renderCardPrice(card);
          row.classList.remove('is-updating');
        }, 180);
      });
    }

    /* ----- filtering ----- */
    function matches(card) {
      var t = state.types === 'all' || (' ' + card.dataset.types + ' ').indexOf(' ' + state.types + ' ') !== -1;
      var g = state.goals === 'all' || (' ' + card.dataset.goals + ' ').indexOf(' ' + state.goals + ' ') !== -1;
      return t && g;
    }

    function apply() {
      var run = function () {
        var visible = 0;
        cards.forEach(function (card) {
          var show = matches(card);
          card.hidden = !show;
          if (show) visible++;
        });
        var filtered = state.types !== 'all' || state.goals !== 'all';
        if (countLine) {
          countLine.textContent = filtered
            ? visible + ' of ' + total + ' Treatments'
            : total + ' Bookable Treatments';
        }
        if (empty) empty.hidden = visible !== 0;
        chips.forEach(function (c) {
          var on = state[c.dataset.group] === c.dataset.key;
          c.classList.toggle('is-active', on);
          c.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      };
      if (reduceMotion || !grid) { run(); return; }
      grid.classList.add('is-updating');
      setTimeout(function () {
        run();
        grid.classList.remove('is-updating');
      }, 150);
    }

    /* ----- URL sync ----- */
    function writeUrl(push) {
      var params = new URLSearchParams(window.location.search);
      ['treatment', 'goal'].forEach(function (k) { params.delete(k); });
      if (state.types !== 'all') params.set('treatment', state.types);
      if (state.goals !== 'all') params.set('goal', state.goals);
      var qs = params.toString();
      var url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
      if (push) window.history.pushState({ hmSvc: true }, '', url);
      else window.history.replaceState(window.history.state, '', url);
    }
    function readUrl() {
      var params = new URLSearchParams(window.location.search);
      state.types = params.get('treatment') || 'all';
      state.goals = params.get('goal') || 'all';
    }
    window.addEventListener('popstate', function () {
      readUrl();
      apply();
    });

    /* ----- wire chips ----- */
    chips.forEach(function (c) {
      c.addEventListener('click', function () {
        state[c.dataset.group] = c.dataset.key;
        writeUrl(true);
        apply();
      });
    });

    /* ----- wire mode radiogroup (click + arrow keys) ----- */
    function setMode(seg) {
      state.mode = seg.dataset.hmSvcMode;
      segs.forEach(function (x) {
        var on = x === seg;
        x.classList.toggle('is-active', on);
        x.setAttribute('aria-checked', on ? 'true' : 'false');
        x.tabIndex = on ? 0 : -1;
      });
      renderPrices();
    }
    segs.forEach(function (s, i) {
      s.addEventListener('click', function () { setMode(s); });
      s.addEventListener('keydown', function (e) {
        var dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
        if (!dir) return;
        e.preventDefault();
        var next = segs[(i + dir + segs.length) % segs.length];
        next.focus();
        setMode(next);
      });
    });

    /* ----- mobile filter sheet ----- */
    var toggle = root.querySelector('[data-hm-svc-filters-toggle]');
    var rail = root.querySelector('[data-hm-svc-rail]');
    if (toggle && rail) {
      toggle.addEventListener('click', function () {
        var open = rail.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
    Array.prototype.forEach.call(root.querySelectorAll('[data-hm-svc-clear]'), function (clear) {
      clear.addEventListener('click', function () {
        state.types = 'all';
        state.goals = 'all';
        writeUrl(true);
        apply();
        if (rail) rail.classList.remove('is-open');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });
    });

    /* ----- book bar yields at footer ----- */
    var book = root.querySelector('[data-hm-svc-book]');
    var footer = document.querySelector('.footer');
    if (book && footer) {
      var setYield = function (on) { book.classList.toggle('is-yielded', on); };
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) { setYield(entry.isIntersecting); });
        }, { threshold: 0 }).observe(footer);
      }
      var ticking = false;
      window.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          setYield(footer.getBoundingClientRect().top < window.innerHeight);
        });
      }, { passive: true });
    }

    /* ----- boot: state from URL, server markup already = NEW CLIENT default ----- */
    readUrl();
    apply();
    if (state.mode !== 'new-client') renderPrices();
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-hm-svc]').forEach(init);
  }
  function start() {
    watchHeader();
    watchPreviewBar();
    boot();
  }
  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
